import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { Spinner } from '../../ui/Spinner';
import { useAccount } from '../../contexts/AccountContext';
import { founder } from '../../data/content';
import { btnPrimary, inputCls, labelCls } from '../../ui/styles';
import { ease } from '../../core/format/motion';
import { PASSWORD_HINT, PASSWORD_MAX, passwordProblem } from '../../core/rules/password';
import { orderLabel } from '../../utils/accountOrders';
import type { AccountOrder } from '../../core/types/account';
type Props = {
  email: string;
  firstName: string;
  orderId: string;
  order?: AccountOrder;
  subscribeIds?: string[];
  intervalWeeks?: number;
};
const ERROR_ID = 'after-password-error';
const HINT_ID = 'after-password-hint';
const ORDERS_PATH = '/account/bestellingen';
const benefits = ['Zien waar je pakket is', 'Een seintje als een product bijna op is', 'Later opnieuw bestellen in één tik'];

/**
 * De volgende stap na de betaling, als duidelijke kaart: ingelogd → naar je bestelling;
 * gast → één veld om een account te maken (de bestelling staat er dan al in).
 * Nooit vóór het betalen: afrekenen blijft altijd zonder account mogelijk.
 */
export function AccountAfterCheckout({
  email,
  firstName,
  order,
  subscribeIds,
  intervalWeeks
}: Props) {
  const {
    signedIn,
    register
  } = useAccount();
  const reduce = useReducedMotion();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const weak = passwordProblem(password, {
      email,
      firstName
    });
    if (weak) {
      setError(weak);
      document.getElementById('after-password')?.focus();
      return;
    }
    setBusy(true);
    setError('');
    const r = await register({
      firstName,
      email,
      password,
      claim: order ? {
        order,
        subscribeIds: subscribeIds ?? [],
        intervalWeeks
      } : undefined
    });
    setBusy(false);
    if (r.ok) setDone(true);else setError(r.error ?? 'Dat lukte even niet. Probeer het nog eens.');
  };

  /** Bestelnummer en status: het tastbare bewijs dat je bestelling ergens "staat". */
  const orderStatus = (tone: 'dark' | 'light') => <p className={`inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] ${tone === 'dark' ? 'text-shell/75' : 'text-muted'}`} data-id="element-5858" data-mp="1hza9trfn2zbg">
      <span data-id="element-5859" data-mp="1rvwuog7ytqdh">
        Bestelling <span className={tone === 'dark' ? 'text-shell' : 'text-ink'} data-id="element-5860" data-mp="1rvnd9h0zkrje">{orderLabel(order ?? {})}</span>
      </span>
      <span aria-hidden="true" data-id="element-5861" data-mp="1ww18tzlei90j">·</span>
      <span className="inline-flex items-center gap-1.5" data-id="element-5862" data-mp="1wswzyowzqp3j">
        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-clay" data-id="element-5863" data-mp="33a0nhj2th8gy" />
        In behandeling
      </span>
    </p>;
  return <aside aria-labelledby="after-title" data-id="element-5864" data-mp="38c9fod7v86i0">
      <AnimatePresence mode="wait" initial={false} data-id="element-5865" data-mp="wnbf7vzfhzqc">
        {signedIn || done ?
      // Het zwaarste blok op de pagina: donker vlak, één lichte knop. Hier ga je hierna heen.
      <motion.div key="account" role={done ? 'status' : undefined} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="bg-ink p-6 text-shell sm:p-8" data-id="element-5866" data-mp="mwqike3bf5a2">
            {orderStatus('dark')}
            <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-5867" data-mp="8vxjdg22jm0m">
              <div className="max-w-sm" data-id="element-5868" data-mp="3ye7gmsuflbu">
                <h2 id="after-title" className="font-display text-[1.75rem] font-light leading-tight" data-id="element-5869" data-mp="3v4p2a179fpuq">
                  {done ? 'Je account staat klaar, met je bestelling erin.' : 'Volg je bestelling in Mijn omgeving.'}
                </h2>
                <p className="mt-2 text-[15px] leading-relaxed text-shell/75" data-id="element-5870" data-mp="1tediyajxpmun">
                  Je status en gebruiksadvies op één plek. Zodra {founder.firstName} je pakket verstuurt, verschijnt daar ook de track & trace.
                </p>
              </div>
              <Link to={ORDERS_PATH} className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-shell px-7 py-4 text-[15px] text-ink transition-colors duration-150 ease-soft hover:bg-blush" data-id="element-5871" data-mp="h1zyg6z1low9">
                Bekijk je bestelling
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5872" data-mp="c2nfvyf7f3zh" />
              </Link>
            </div>
            <Link to="/account" className="mt-6 inline-flex py-1 text-[14px] text-shell/70 underline-offset-4 transition-colors duration-150 ease-soft hover:text-shell hover:underline" data-id="element-5873" data-mp="3uhxua8ijpp1y">
              Naar het overzicht van Mijn omgeving
            </Link>
          </motion.div> : <motion.form key="form" onSubmit={submit} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease
      }} noValidate className="grid gap-8 bg-shell p-6 ring-1 ring-line sm:grid-cols-2 sm:p-8" data-id="element-5874" data-mp="35vi4yiaohl1t">
            <div data-id="element-5875" data-mp="3frl5jjpvtbmn">
              {orderStatus('light')}
              <h2 id="after-title" className="mt-4 font-display text-[1.75rem] font-light leading-tight text-ink" data-id="element-5876" data-mp="3atjm2xojnaan">
                Volg je bestelling in Mijn omgeving.
              </h2>
              <ul className="mt-4 space-y-2" data-id="element-5877" data-mp="3ktk9aupjaak4">
                {benefits.map(b => <li key={b} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-5878" data-mp="2fb0i9kfvjrz">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-5879" data-mp="3tkkknklr53to" />
                    {b}
                  </li>)}
              </ul>
            </div>
            <div className="sm:border-l sm:border-line sm:pl-8" data-id="element-5880" data-mp="7exe4k46j21b">
            <p className="text-[13px] text-muted" data-id="element-5881" data-mp="eh2n18tz13u2">
              Je bestelling staat er al in. Account voor <span className="text-ink" data-id="element-5882" data-mp="jg5fltsrlhl5">{email}</span>
            </p>
            <label htmlFor="after-password" className={`${labelCls} mt-3`} data-id="element-5883" data-mp="jgf7jmxddyth">
              Kies een wachtwoord
            </label>
            <div className="relative" data-id="element-5884" data-mp="t5yrkvwtjypx">
              <input id="after-password" maxLength={PASSWORD_MAX} type={show ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={e => {
              setPassword(e.target.value);
              if (error && e.target.value.length >= 8) setError('');
            }} aria-invalid={error ? true : undefined} aria-describedby={error ? `${HINT_ID} ${ERROR_ID}` : HINT_ID} className={`${inputCls} pr-12`} data-id="element-5885" data-mp="t70cds3mzzku" />
              <button type="button" onClick={() => setShow(v => !v)} aria-label={show ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'} aria-pressed={show} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-5886" data-mp="3dx8y8qsgcpcs">
                {show ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5887" data-mp="2rotr7e0rb8hy" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5888" data-mp="3pc2k3u337jtz" />}
              </button>
            </div>
            <p id={HINT_ID} className="mt-1.5 text-[13px] text-muted" data-id="element-5889" data-mp="3kdhknwvnt46l">
              {PASSWORD_HINT}
            </p>
            {error && <p id={ERROR_ID} role="alert" className="mt-2 text-[13px] text-clay" data-id="element-5890" data-mp="3fffwwynaggos">
                {error}
              </p>}
            <button type="submit" disabled={busy} className={`${btnPrimary} mt-5 w-full py-4 text-[15px]`} data-id="element-5891" data-mp="23yprhhqovsw8">
              {busy && <Spinner data-id="element-5892" data-mp="1yy22t2pxno56" />}
              {busy ? 'Je account wordt gemaakt…' : 'Maak account en volg je bestelling'}
            </button>
            <p className="mt-3 text-center text-[13px] text-muted" data-id="element-5893" data-mp="2opbt5gg1kyil">Liever niet? Prima, je bevestiging komt ook zo per mail.</p>
            </div>
          </motion.form>}
      </AnimatePresence>
    </aside>;
}